/* hp26 v5u (draft) – HP26 Collection tabs: accessible tabs + one-row carousel.
   First tab is server-rendered; other tabs come from the Section Rendering API
   (/collections/<handle>?section_id=hp26-ctabs-panel), prefetched on hover/focus/touch and cached. */
(() => {
  const cache = new Map();
  const load = (url) => {
    if (!cache.has(url)) {
      const u = url + (url.includes('?') ? '&' : '?') + 'section_id=hp26-ctabs-panel';
      cache.set(url, fetch(u, { credentials: 'same-origin' }).then((r) => {
        if (!r.ok) throw new Error(r.status);
        return r.text();
      }).then((html) => {
        const d = new DOMParser().parseFromString(html, 'text/html');
        const ul = d.querySelector('[data-ct-items]');
        if (!ul) throw new Error('no items');
        return ul.innerHTML;
      }).catch((e) => { cache.delete(url); throw e; }));
    }
    return cache.get(url);
  };
  const init = (root) => {
    if (root.dataset.ctReady) return;
    root.dataset.ctReady = '1';
    const tabs = [...root.querySelectorAll('[role="tab"]')];
    const panel = root.querySelector('[role="tabpanel"], .hp26-ct__panel');
    const track = root.querySelector('[data-ct-track]');
    const prev = root.querySelector('[data-ct-prev]');
    const next = root.querySelector('[data-ct-next]');
    const all = root.querySelector('[data-ct-all]');
    const allName = root.querySelector('[data-ct-all-name]');
    const limit = parseInt(root.dataset.limit, 10) || 10;
    if (!track) return;

    const arrows = () => {
      const max = track.scrollWidth - track.clientWidth - 2;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max;
      const img = track.querySelector('.card__media, .media, img');
      if (img) root.style.setProperty('--ct-imgh', img.getBoundingClientRect().height + 'px');
    };
    const step = (dir) => {
      const item = track.querySelector('.hp26-ct__item');
      const w = item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 16) : track.clientWidth;
      const n = Math.max(1, Math.floor((track.clientWidth + 4) / w));
      track.scrollBy({ left: dir * n * w, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    };
    prev && prev.addEventListener('click', () => step(-1));
    next && next.addEventListener('click', () => step(1));
    track.addEventListener('scroll', () => requestAnimationFrame(arrows), { passive: true });
    let rw = innerWidth;
    addEventListener('resize', () => { if (innerWidth !== rw) { rw = innerWidth; if (panel) panel.style.minHeight = ''; } arrows(); }, { passive: true });
    arrows();
    if (document.readyState !== 'complete') addEventListener('load', arrows, { once: true });

    // Arrows-only mode (e.g. HP26 Deals) — no tab pills.
    if (!tabs.length) return;
    warmSoon(root);
    const sel = tabs.find((t) => t.getAttribute('aria-selected') === 'true') || tabs[0];
    cache.set(sel.dataset.url, Promise.resolve(track.innerHTML));
    let req = 0;

    const select = (tab, focus) => {
      tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', on);
        t.tabIndex = on ? 0 : -1;
      });
      if (focus) tab.focus();
      tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      panel.setAttribute('aria-labelledby', tab.id);
      if (all) all.href = tab.dataset.url;
      if (allName) allName.textContent = ' ' + tab.dataset.title;
      const my = ++req;
      // Keep the panel at least as tall as it is now so nothing below moves while loading.
      panel.style.minHeight = panel.offsetHeight + 'px';
      panel.setAttribute('aria-busy', 'true');
      load(tab.dataset.url).then((html) => {
        if (my !== req) return;
        const tmp = document.createElement('ul');
        tmp.innerHTML = html;
        [...tmp.querySelectorAll('.hp26-ct__item')].slice(limit).forEach((li) => li.remove());
        track.innerHTML = tmp.innerHTML;
        track.scrollLeft = 0;
        panel.removeAttribute('aria-busy');
        if (typeof window.initLazyImages === 'function') window.initLazyImages();
        arrows();
        document.dispatchEvent(new CustomEvent('hp26:ctabs:swap', { detail: { root } }));
        // keep the lock (only grows) so a slow response never shifts the page; released on resize
        panel.style.minHeight = Math.max(panel.offsetHeight, parseFloat(panel.style.minHeight) || 0) + 'px';
      }).catch(() => {
        if (my === req) location.href = tab.dataset.url;
      });
    };

    tabs.forEach((t, i) => {
      t.addEventListener('click', (e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
        e.preventDefault();
        if (t.getAttribute('aria-selected') !== 'true') select(t, false);
      });
      const warm = () => { load(t.dataset.url).catch(() => {}); };
      t.addEventListener('pointerenter', warm, { passive: true });
      t.addEventListener('touchstart', warm, { passive: true });
      t.addEventListener('focus', warm);
      t.addEventListener('keydown', (e) => {
        let j = null;
        if (e.key === 'ArrowRight') j = (i + 1) % tabs.length;
        else if (e.key === 'ArrowLeft') j = (i - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home') j = 0;
        else if (e.key === 'End') j = tabs.length - 1;
        else if (e.key === ' ' ) { e.preventDefault(); t.click(); return; }
        if (j === null) return;
        e.preventDefault();
        select(tabs[j], true);
      });
    });
  };
  const warmSoon = (root) => {
    // Prefetch the next two tabs once the section is near the viewport (others on hover/focus/touch).
    const urls = [...root.querySelectorAll('[role="tab"]')].slice(1, 3).map((t) => t.dataset.url);
    const go = () => urls.forEach((u) => load(u).catch(() => {}));
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((es) => {
      if (es.some((e) => e.isIntersecting)) { io.disconnect(); (window.requestIdleCallback || setTimeout)(go); }
    }, { rootMargin: '600px 0px' });
    io.observe(root);
  };
  const boot = () => document.querySelectorAll('[data-hp26-ct]').forEach(init);
  boot();
  document.addEventListener('shopify:section:load', boot);
})();
